// Expense Report Page
import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Wallet, TrendingDown, Receipt, CreditCard } from 'lucide-react';
import { useExpenseReport } from '@/features/reports/hooks/useExpenseReport';
import { PeriodSelector } from '@/features/reports/components/PeriodSelector';
import { MetricCard } from '@/features/reports/components/MetricCard';
import { ExportButton } from '@/features/reports/components/ExportButton';
import { ExpenseTrendChart, ExpensesByCategoryChart } from '@/features/reports/components/ExpenseChart';
import { getThisMonth } from '@/features/reports/utils/periods';
import { formatCurrency } from '@/features/reports/utils/calculations';
import type { ExportFormat } from '@/types/report';

export const Route = createFileRoute('/apps/reports/expenses')({  
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: ExpenseReportPage,
});

function ExpenseReportPage() {
  const [period, setPeriod] = useState(getThisMonth());
  const { data, isLoading, error } = useExpenseReport(period);

  const handleExport = async (format: ExportFormat) => {
    console.log('Exporting expense report as', format);
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Erreur lors du chargement du rapport: {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <span className="font-arabic">تقرير المصاريف</span>
            <span className="text-muted-foreground mx-2">/</span>
            <span>Rapport des dépenses</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Analyse détaillée de vos dépenses et coûts d'exploitation
          </p>
        </div>
        
        <ExportButton onExport={handleExport} disabled={isLoading || !data} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Period Selector */}
        <div className="lg:col-span-1">
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>

        {/* Metrics */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="Dépenses totales TTC"
            titleAr="المصاريف الإجمالية"
            value={data?.metrics.totalExpenses || 0}
            format="currency"
            icon={Wallet}
            trend={data?.metrics.growthRate}
            color="red"
          />
          
          <MetricCard
            title="Dépenses HT"
            titleAr="المصاريف الصافية"
            value={data?.metrics.totalExpensesHT || 0}
            format="currency"
            icon={TrendingDown}
            color="orange"
          />
          
          <MetricCard
            title="TVA déductible"
            titleAr="الضريبة القابلة للخصم"
            value={data?.metrics.totalTVA || 0}
            format="currency"
            icon={Receipt}
            subtitle="TVA 19%"
            color="purple"
          />
          
          <MetricCard
            title="Nombre de transactions"
            titleAr="عدد العمليات"
            value={data?.metrics.transactionCount || 0}
            format="number"
            icon={CreditCard}
            subtitle={`Moyenne: ${formatCurrency(data?.metrics.averageExpense || 0)}`}
            color="cyan"
          />
        </div>
      </div>

      {/* Charts */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-80 bg-card border border-border rounded-lg animate-pulse" />
          ))}
        </div>
      ) : data ? (
        <div className="space-y-6">
          <ExpenseTrendChart data={data.trend} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ExpensesByCategoryChart data={data.byCategory} />
            
            {/* Category Breakdown Table */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                <span className="font-arabic">تفصيل حسب الفئة</span>
                <span className="text-muted-foreground mx-2">/</span>
                <span>Détail par catégorie</span>
              </h3>
              
              <div className="space-y-2">
                {data.byCategory.map((category, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-background rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {category.category}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {category.count} transaction{category.count > 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(category.amount)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {category.percentage.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Suppliers */}
          {data.bySupplier.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                <span className="font-arabic">أكبر الموردين</span>
                <span className="text-muted-foreground mx-2">/</span>
                <span>Principaux fournisseurs</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.bySupplier.slice(0, 10).map((supplier, index) => (
                  <div
                    key={supplier.supplierId}
                    className="flex items-center justify-between p-3 bg-background rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {supplier.supplierName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {supplier.count} transaction{supplier.count > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(supplier.amount)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
      </div>
    </DashboardLayout>
  );
}
